import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Mail, Lock, Briefcase, GraduationCap, FileText, Loader2, ArrowRight } from 'lucide-react';
import api from '../api';

const Signup = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    career: '',
    education: '',
    resume: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Basic validation
    if (!form.name || !form.email || !form.password) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    try {
      await api.post('/auth/signup', form);
      // Auto login or redirect to login? Prompt implies redirect/alert
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.error || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-2xl"
      >
        <div className="card p-8 md:p-10 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 to-violet-500"></div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">Create an Account</h2>
            <p className="text-slate-500 dark:text-slate-400">Join thousands of job seekers and find your dream career</p>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-6 border border-red-100 text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-1 md:col-span-2 space-y-1.5">
              <label className="input-label" htmlFor="name">Full Name <span className="text-red-500">*</span></label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <User className="h-5 w-5" />
                </div>
                <input
                  id="name"
                  name="name"
                  placeholder="John Doe"
                  className="input-field pl-10"
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="input-label" htmlFor="email">Email Address <span className="text-red-500">*</span></label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <Mail className="h-5 w-5" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  className="input-field pl-10"
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="input-label" htmlFor="password">Password <span className="text-red-500">*</span></label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <Lock className="h-5 w-5" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  className="input-field pl-10"
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="col-span-1 md:col-span-2 border-t border-slate-100 dark:border-slate-700 pt-4 mt-2">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-4 uppercase tracking-wider">Professional Profile (Optional)</h3>
            </div>

            <div className="space-y-1.5">
              <label className="input-label" htmlFor="career">Career Interest</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <Briefcase className="h-5 w-5" />
                </div>
                <input
                  id="career"
                  name="career"
                  placeholder="e.g. Frontend Developer"
                  className="input-field pl-10"
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="input-label" htmlFor="education">Education</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <GraduationCap className="h-5 w-5" />
                </div>
                <input
                  id="education"
                  name="education"
                  placeholder="e.g. BSc Computer Science"
                  className="input-field pl-10"
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="col-span-1 md:col-span-2 space-y-1.5">
              <label className="input-label" htmlFor="resume">Resume URL</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <FileText className="h-5 w-5" />
                </div>
                <input
                  id="resume"
                  name="resume"
                  placeholder="Link to your resume (Google Drive, LinkedIn, etc.)"
                  className="input-field pl-10"
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="col-span-1 md:col-span-2 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary flex items-center justify-center gap-2 group h-12 text-lg"
              >
                {loading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <>
                    Create Account
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-8 text-center text-sm text-slate-600 dark:text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
              Log in instead
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Signup;
